import {keepPreviousData, useMutation, useQuery} from "@tanstack/react-query";
import {useShowError} from "@hooks/useShowError/useShowError";
import {
	addCameraApi,
	deleteCameraApi,
	getCameraLastSnapApi,
	getCamerasListApi,
	getCamerasDashboardApi,
	getCameraSnapsByTimeApi,
	updateCameraApi
} from "@containers/cameras/api/api";
import {
	QUERY_KEY_CAMERAS,
	QUERY_KEY_CAMERAS_DASHBOARD,
	QUERY_KEY_CAMERAS_LAST_SNAP,
	QUERY_KEY_CAMERAS_SNAPS_BY_TIME
} from "@containers/cameras/queryHooks/constants/queryKey";
import {useSnackBar} from "@hooks/alert/useSnackBar";
import {useTranslation} from "react-i18next";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {refetchCameras} from "@containers/cameras/refetchCameras";


export const useGetCamerasListQuery = ({
										   accountId,
										   deviceId,
									   }: {
	accountId: number
	deviceId?: number
}) => {
	
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getCamerasListApi({accountId, deviceId}, onShowError),
		queryKey: [QUERY_KEY_CAMERAS, accountId?.toString(), deviceId || 0],
		staleTime: 1000 * 60 * 30,
		enabled: !!accountId,
		placeholderData: keepPreviousData,
	});
}


export const useGetCamerasDashboardQuery = (accountId: number) => {
	const {onShowError} = useShowError()
	return useQuery({
		queryFn: () => getCamerasDashboardApi(accountId, onShowError),
		queryKey: [QUERY_KEY_CAMERAS_DASHBOARD, accountId?.toString()],
		staleTime: 1000 * 60,
		enabled: !!accountId,
		placeholderData: keepPreviousData,
		refetchInterval: 1000 * 60,
		refetchIntervalInBackground: false,
	})
}


export const useAddCameraQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	
	const {t} = useTranslation()
	
	return useMutation({
		mutationFn: addCameraApi,
		onSuccess: () => {
			refetchCameras()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'success'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	});
}

export const useUpdateCameraQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	
	const {t} = useTranslation()
	
	return useMutation({
		mutationFn: updateCameraApi,
		onSuccess: () => {
			refetchCameras()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'success'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	});
}

export const useDeleteCameraQuery = ({onSuccess}: {
	onSuccess?: () => void
} = {}) => {
	const {setSnackBar} = useSnackBar()
	
	const {t} = useTranslation()
	
	return useMutation({
		mutationFn: deleteCameraApi,
		onSuccess: () => {
			refetchCameras()
			setSnackBar({
				title: t('base.successSaveMessage'),
				variant: 'success'
			})
			onSuccess && onSuccess()
		},
		onError: (e) => {
			const message = buildErrorsMessages(e)
			setSnackBar({
				message,
				variant: 'error'
			})
		}
	});
}

export const useGetCameraSnapsByTimeQuery = ({
												 accountId,
												 deviceId,
												 cameraNumber,
												 dateTo,
												 dateFrom,
												 enabled,
											 }: {
	accountId: number
	deviceId?: number
	dateTo: string
	dateFrom: string
	cameraNumber?: number
	enabled?: boolean
}) => {
	
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getCameraSnapsByTimeApi({
			accountId,
			deviceId,
			cameraNumber,
			dateTo,
			dateFrom
		}, onShowError),
		queryKey: [
			QUERY_KEY_CAMERAS_LAST_SNAP,
			accountId?.toString(),
			deviceId || 0,
			dateFrom || '',
			dateTo || '',
			cameraNumber || 0
		],
		staleTime: 1000 * 60 * 30,
		enabled: typeof enabled !== 'undefined' ? enabled : true && !!accountId && !!deviceId && !!dateTo && !!dateFrom,
		placeholderData: keepPreviousData,
	});
}

export const useGetCameraLastSnapQuery = ({
											  accountId,
											  deviceId,
											  cameraNumber,
											  refetchInterval,
											  enabled
										  }: {
	accountId: number
	deviceId?: number
	cameraNumber?: number
	refetchInterval?: number
	enabled?: boolean
}) => {
	
	const {onShowError} = useShowError()
	
	return useQuery({
		queryFn: () => getCameraLastSnapApi({
			accountId,
			deviceId,
			cameraNumber,
		}, onShowError),
		queryKey: [
			QUERY_KEY_CAMERAS_SNAPS_BY_TIME,
			accountId?.toString(),
			deviceId || 0,
			cameraNumber || 0
		],
		staleTime: 1000 * 60 * 30,
		enabled:typeof enabled === 'undefined' ? true : enabled && !!accountId && !!deviceId,
		placeholderData: keepPreviousData,
		refetchInterval
	});
}
